/**
 * CrmLossReasons - Tela de gestão de Motivos de Perda do CRM
 *
 * Padrão visual alinhado a CrmLeadSources (header compacto + card com tabela +
 * badge de status via StatusDot).
 *
 * RELEASE: 2026-07-10 v1.0.0
 */
import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCrmLossReasons,
  useDeleteLossReason,
  type LossReasonEntry,
} from '@/hooks/useCrmLossReasons';
import { LossReasonFormDialog } from '@/components/crm/loss-reasons/LossReasonFormDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';

export default function CrmLossReasons() {
  const { reasons, isLoading, refetch } = useCrmLossReasons(true);
  const del = useDeleteLossReason();
  const { canCreate: pCanCreate, canEdit: pCanEdit, canDelete: pCanDelete } = usePermissions();

  const canCreate = pCanCreate('crm_param_loss_reasons');
  const canEdit = pCanEdit('crm_param_loss_reasons');
  const canDelete = pCanDelete('crm_param_loss_reasons');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LossReasonEntry | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LossReasonEntry | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reasons;
    return reasons.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        (s.description ?? '').toLowerCase().includes(term),
    );
  }, [reasons, search]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (s: LossReasonEntry) => {
    setEditing(s);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await del.mutateAsync(pendingDelete.id);
    setPendingDelete(null);
    refetch();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Motivos de Perda</h1>
        </div>
        {canCreate && (
          <Button
            size="xs"
            onClick={openCreate}
            className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5" />
            Novo Motivo
          </Button>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar motivos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {!isLoading && filtered.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <XCircle className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum motivo cadastrado</p>
              <p className="text-sm mt-1">
                {search
                  ? 'Tente ajustar sua busca'
                  : 'Cadastre o primeiro motivo para começar a controlar perdas'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Cor</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="w-[90px] text-center">Ordem</TableHead>
                  <TableHead className="w-[100px] text-center">Usos</TableHead>
                  <TableHead className="w-[120px] text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando motivos...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  filtered.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div
                          className="h-6 w-6 rounded-full ring-1 ring-border"
                          style={{ backgroundColor: s.color ?? '#9ca3af' }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm truncate max-w-[320px]">
                        {s.description || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={s.is_active ? 'emerald' : 'muted'} />
                          {s.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-sm">
                        {s.sort_order}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-sm">
                        {s.usage_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEdit(s)}
                              title="Editar"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setPendingDelete(s)}
                              title="Excluir"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <LossReasonFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir motivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir o motivo <strong>{pendingDelete?.name}</strong>.
              Se houver negócios utilizando este motivo, ele será apenas desativado
              (continuará visível nos negócios existentes, mas não aparecerá em novos).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
